
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { Apollo, Notify } from '@/core/services';
import { GET_CUSTOMER, SEARCH_CUSTOMER, LOGIN_AS, SYNC_CUSTOMER_AGREEMENT } from '../graphql/Queries';
import { DELETE_CUSTOMER } from '../graphql/Mutations';
import Search from '../../../../components/search/Search.vue';
import AdvanceSearch from '../../../../components/search/AdvanceSearch.vue';
import Table from '../../../../components/Table/Table.vue';
import { generateAvatar } from '@/core/helpers/generateAvatar';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { setPageHistory } from '@/core/helpers/toolbar';
import InnerLoader from '../../../../components/InnerLoader.vue';
import { dropdown_handler } from '../../../../core/helpers/dropdownHandler';
import { GET_CUSTOMER_GROUP } from '@/modules/customer/customer-groups/graphql/Queries';

export default defineComponent({
    components: {
        // Search,
        AdvanceSearch,
        Table,
        InnerLoader
    },
    setup() {
        const i18n = useI18n();
        const store = useStore();
        const loader = ref(true);
        const loading = ref(false);
        const pagination = ref({}) as Record<any, any>;
        const permissions = store.getters.getPermissions;
        const customers: any = ref([]);
        const clickHandler = ref() as Record<any, any>;
        const currentPage = ref(0);
        const system_locale = ref();
        const router = useRouter();
        const activeIndex = ref(-1);
        const search_customer_query = ref(false);
        const columnName = ref(null);
        const columnSort = ref(null);
        const customerGroupList = ref([]) as Record<any, any>;
        const agreementTypeList = ref([
            {
                label: 'message.ALL',
                value: 'all'
            },
            {
                label: 'Rental Agreement Customer',
                value: 'RENTAL'
            },
            {
                label: 'Service Agreement Customer',
                value: 'SERVICE'
            },
            {
                label: 'Non-Agreement Customer',
                value: 'NON'
            }
        ]);
        const filterOption = ref({}) as Record<any, any>;
        const sites = ref([]) as Record<any, any>;

        filterOption.value = {
            status: true,
            customer_group: true,
            economic_number: true,
            name: true,
            email: true,
            agreement_type: true,
            region: true,
            customer_filter_row: true,
            sync_customer_agreement: true
        };

        const statusList = ref([
            {
                label: 'message.ALL',
                value: 'all'
            },
            {
                label: 'message.ACTIVE',
                value: 1
            },
            {
                label: 'message.INACTIVE',
                value: 0
            }
        ]);
        filterOption.value.status = true;

        const columns = ref([
            {
                label: '',
                key: 'avatar',
                width: true
            },
            {
                label: 'message.NAME',
                key: 'name',
                sorting: true
            },
            {
                label: 'message.ROLE',
                key: 'roles',
                width: true
            },
            {
                label: 'message.ACCOUNT_ECONOMIC_NUMBER',
                key: 'economic_number',
                sorting: true,
                width: true
            },
            {
                label: 'message.CUSTOMER_GROUP',
                key: 'customer_group',
                width: true
            },
            {
                label: 'message.AGREEMENT',
                key: 'agreement_type',
                width: true
            },
            {
                label: 'message.REGION',
                key: 'site',
                width: true
            },
            {
                label: 'message.STATUS',
                key: 'status',
                width: true
            },
            {
                label: 'message.ACTIONS',
                key: 'actions',
                textAlignment: 'center',
                width: true
            }
        ]);

        const filterObject = ref([
            {
                label: 'message.NAME',
                value: 'name'
            },
            {
                label: 'message.EMAIL',
                value: 'email'
            }
        ]);

        const handleCurrentChange = (val: number) => {
            clickHandler.value(val);
        };

        const dropdownMenuStyle = ref({});
        const dropdownMenuRef = ref<HTMLElement | null>(null);
        const dropdownIsDark = ref(false);
        let dropdownButton: HTMLElement | null = null;
        const activeCustomer = computed(() => customers.value?.find((customer: any) => customer?.id === activeIndex.value) || null);

        const closeDropdown = () => {
            activeIndex.value = -1;
            dropdownButton = null;
            dropdownMenuStyle.value = {};
        };

        const placeDropdown = (button: HTMLElement) => {
            const menu = dropdownMenuRef.value;
            if (!menu) return;
            const rect = button.getBoundingClientRect();
            const menuHeight = menu.offsetHeight || 160;
            const menuWidth = menu.offsetWidth || 160;
            const gap = 6;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUp = spaceBelow < menuHeight + gap && rect.top > menuHeight + gap;
            let top = openUp ? rect.top - menuHeight - gap : rect.bottom + gap;
            let left = rect.right - menuWidth;

            if (left < 8) left = 8;
            if (left + menuWidth > window.innerWidth - 8) left = Math.max(8, window.innerWidth - menuWidth - 8);
            if (top < 8) top = 8;
            if (top + menuHeight > window.innerHeight - 8) top = Math.max(8, window.innerHeight - menuHeight - 8);

            dropdownMenuStyle.value = {
                display: 'block',
                position: 'fixed',
                top: `${top}px`,
                left: `${left}px`,
                right: 'auto',
                bottom: 'auto',
                zIndex: 2000,
                margin: 0
            };
        };

        const dropdownIsVisible = (button: HTMLElement) => {
            const container = button.closest('.table-responsive');
            if (!container) return true;
            const buttonRect = button.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            return buttonRect.bottom > containerRect.top && buttonRect.top < containerRect.bottom && buttonRect.right > containerRect.left && buttonRect.left < containerRect.right;
        };

        const syncDropdownPosition = () => {
            if (!dropdownButton) return;
            if (!dropdownIsVisible(dropdownButton)) {
                closeDropdown();
                return;
            }
            placeDropdown(dropdownButton);
        };

        const dropdownHandler = (index, id, event) => {
            if (index === id) {
                closeDropdown();
                return;
            }
            activeIndex.value = id;
            dropdownButton = event.currentTarget;
            dropdownIsDark.value = !!document.querySelector('.layout-dark');
            nextTick(() => {
                if (dropdownButton) placeDropdown(dropdownButton);
            });
        };
        const handleClickOutside = event => {
            if (!event.target.closest('.action-btn') && !event.target.closest('.customers-action-menu')) {
                closeDropdown();
            }
        };

        const getCustomerGroups = () => {
            search_customer_query.value = false;
            // loader.value = true;
            Apollo.watchQuery({
                query: GET_CUSTOMER_GROUP,
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'network-only'
            }).subscribe(({ data }) => {
                customerGroupList.value = [];
                data.customer_groups.data.forEach(ele => {
                    customerGroupList.value.push({
                        label: ele.name,
                        value: ele.id
                    });
                });
                // loader.value = false;
                filterOption.value.customer_group = true;
            });
        };

        getCustomerGroups();

        const getCustomers = (page = 1, reload = false, colName = null, type = null) => {
            loader.value = true;
            columnName.value = null;
            columnSort.value = null;
            search_customer_query.value = false;
            Apollo.watchQuery({
                query: GET_CUSTOMER,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only',
                variables: {
                    page: page,
                    limit: 10
                    // col: colName,
                    // type: type == true ? "asc" : "desc",
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                reload == true ? (currentPage.value = 1) : false;
                clickHandler.value = getCustomers;
                customers.value = data.customers.data;
                pagination.value = data.customers;
                loader.value = false;
            });
        };

        const handleEdit = (user: Record<any, any>) => {
            const searchData = store.getters.getSearchData;
            setPageHistory({
                filter: searchData?.data?.select,
                search_key: searchData?.data?.input,
                page_number: pagination.value?.current_page
            });
            router.push({ name: 'edit_customer', params: { uuid: user.uuid } });
        };

        const handleView = (val: Record<any, any>) => {
            const searchData = store.getters.getSearchData;
            setPageHistory({
                filter: searchData?.data?.select,
                search_key: searchData?.data?.input,
                page_number: pagination.value?.current_page
            });
            router.push({
                name: 'customer_detail',
                params: { uuid: val.uuid }
            });
        };

        const handleLoginAs = (customer: Record<any, any>) => {
            loading.value = true;
            const login_as = Apollo.watchQuery({
                query: LOGIN_AS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only',
                variables: {
                    uuid: customer?.user.uuid
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loading.value = false;
                }

                if (data?.login_as != null) window.open(`${process.env.VUE_APP_GEP_SHOP}/auth/attempt-login/${data.login_as}`, '_blank');
                login_as.unsubscribe();
                data = null;
                loading.value = false;
            });
        };

        const searchHandler = (page = 1, reload = false, colName = null, type = null) => {
            if (colName != null) columnName.value = colName;
            if (type != null) columnSort.value = type;
            search_customer_query.value = true;
            const searchData = store.getters.getSearchData;
            loader.value = true;
            Apollo.watchQuery({
                query: SEARCH_CUSTOMER,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only',
                variables: {
                    page: page,
                    limit: 10,
                    filter: JSON.stringify(searchData?.data?.select),
                    search_key: searchData?.data?.input,
                    locale: system_locale.value,
                    col: columnName.value,
                    type: columnSort.value == true ? 'asc' : 'desc'
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }

                clickHandler.value = searchHandler;
                customers.value = data?.search_customers?.data;
                pagination.value = data?.search_customers;
                loader.value = false;
            });
        };

        const handleDelete = (id: Record<any, any>) => {
            store.getters.appInstance.$messageBox
                .confirm(`${i18n.t('message.ARE_YOU_SURE_TO_DELETE_RECORD')}?`, i18n.t('message.INFO'), {
                    confirmButtonText: i18n.t('message.OK'),
                    cancelButtonText: i18n.t('message.CANCEL'),
                    type: 'info'
                })
                .then(async () => {
                    loader.value = true;
                    await Apollo.mutate({
                        mutation: DELETE_CUSTOMER,
                        variables: { id: id },
                        update: (store, { data: { delete_customer } }) => {
                            // const read_data = store.readQuery({
                            // 	query: GET_CUSTOMER,
                            // 	variables: {
                            // 		page: pagination.value.current_page,
                            // 		limit: 10,
                            // 	},
                            // }) as Record<any, any>;
                            // const data = read_data.customers.data.filter((t: any) => t.id !== delete_customer);
                            // store.writeQuery({
                            // 	query: GET_CUSTOMER,
                            // 	data: {
                            // 		customers: {
                            // 			...read_data.customers,
                            // 			data: [...data],
                            // 		},
                            // 	},
                            // 	variables: {
                            // 		page: pagination.value.current_page,
                            // 		limit: 10,
                            // 	},
                            // });
                            searchHandler(pagination.value?.current_page, true, columnName.value, columnSort.value);
                            loader.value = false;
                        }
                    });
                    Notify.success(i18n.t('message.RECORD_DELETED_SUCCESSFULLY'));
                })
                .catch(() => {
                    loader.value = false;
                });
        };

        watchEffect(() => {
            system_locale.value = i18n.fallbackLocale.value;
            sites.value = [];
            const getSites = store.getters.getAllSites?.data?.sites;
            if (getSites) {
                getSites.forEach(element => {
                    sites.value.push({
                        label: element.name,
                        value: element.id
                    });
                });
            }
        });

        const syncCustomerAgreement = () => {
            store.getters.appInstance.$messageBox
                .confirm('Are you sure you want to sync customer agreement?', i18n.t('message.INFO'), {
                    confirmButtonText: i18n.t('message.OK'),
                    cancelButtonText: i18n.t('message.CANCEL'),
                    type: 'info'
                })
                .then(async () => {
                    loading.value = true;
                    const sub = Apollo.watchQuery({
                        query: SYNC_CUSTOMER_AGREEMENT,
                        errorPolicy: 'all',
                        fetchPolicy: 'network-only',
                        nextFetchPolicy: 'cache-only'
                    }).subscribe(({ data, errors }) => {
                        if (errors) {
                            loading.value = false;
                        } else if (data) {
                            if (search_customer_query.value) {
                                searchHandler(pagination.value?.current_page, true, columnName.value, columnSort.value);
                            } else {
                                getCustomers(pagination.value?.current_page || 1);
                            }
                            Notify.success(i18n.t('message.RECORD_SYNCHED_SUCCESSFULLY'));
                            loading.value = false;
                        }
                        loading.value = false;
                        sub.unsubscribe();
                    });
                })
                .catch(() => {
                    loading.value = false;
                });
        };

        const exportFile = data => {
            const searchData = store.getters.getSearchData;
            loading.value = true;
            const sub = Apollo.watchQuery({
                query: SEARCH_CUSTOMER,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                variables: {
                    page: 0,
                    limit: 1,
                    filter: searchData?.data?.select ? JSON.stringify(searchData.data.select) : '',
                    search_key: searchData?.data?.input ? searchData?.data?.input : '',
                    locale: system_locale.value,
                    types: data?.type,
                    date_from: data?.dateFrom,
                    date_to: data?.dateTo
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loading.value = false;
                } else if (data) {
                    window.open(data?.search_customers?.data[0]?.path, '_blank');
                    loading.value = false;
                }
                loading.value = false;
                sub.unsubscribe();
            });
        };

        onMounted(() => {
            const pageHistory = store.getters.getPageHistory;
            document.addEventListener('click', handleClickOutside);
            window.addEventListener('resize', syncDropdownPosition);
            document.addEventListener('scroll', syncDropdownPosition, true);

            if (pageHistory?.filter != undefined || pageHistory?.search_key != undefined) {
                searchHandler(pageHistory.page_number);
            } else {
                pageHistory.page_number == undefined ? getCustomers(1) : getCustomers(pageHistory.page_number);
            }
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', handleClickOutside);
            window.removeEventListener('resize', syncDropdownPosition);
            document.removeEventListener('scroll', syncDropdownPosition, true);
        });

        return {
            customers,
            pagination,
            currentPage,
            filterObject,
            permissions,
            columns,
            loading,
            loader,
            exportFile,
            syncCustomerAgreement,
            handleCurrentChange,
            generateAvatar,
            searchHandler,
            handleEdit,
            handleDelete,
            handleView,
            getCustomers,
            activeIndex,
            activeCustomer,
            dropdownMenuStyle,
            dropdownMenuRef,
            dropdownIsDark,
            dropdownHandler,
            closeDropdown,
            handleClickOutside,
            handleLoginAs,
            filterOption,
            statusList,
            getCustomerGroups,
            customerGroupList,
            agreementTypeList,
            sites
        };
    }
});
